import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Star, Cross, Sun, Moon, Crown, Shield } from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';
import SEOHead from './SEOHead';

const StudiiPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Studii Biblice - Cuvânt și Rugăciune",
    "description": "Studii biblice sistematice și contemplarea profundă a Cuvântului lui Dumnezeu cu Mircea Stanciu",
    "url": "https://cuvantsirugaciune.ro/studii",
    "founder": {
      "@type": "Person",
      "name": "Mircea Stanciu"
    },
    "educationalCredentialAwarded": "Cunoștințe Biblice",
    "teaches": ["Studii Biblice", "Teologie Creștină", "Hermeneutică Biblică", "Spiritualitate Creștină"],
    "audience": {
      "@type": "Audience",
      "audienceType": "Creștini români",
      "geographicArea": {
        "@type": "Country",
        "name": "România"
      }
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const studies = [
    {
      title: "Studiul Săptămânal",
      description: "Explorarea temelor biblice cu aplicații practice pentru viața creștină",
      icon: Sun,
      whatsappLink: "https://chat.whatsapp.com/JDVNz6oeuN9KQljJm7vxyY",
      topics: ["Cărțile Bibliei", "Parabolele lui Isus", "Psalmii", "Epistolele Pauline"]
    },
    {
      title: "Meditații Zilnice",
      description: "Reflecții scurte și profunde pentru hrănirea spirituală zilnică",
      icon: Moon,
      whatsappLink: "https://chat.whatsapp.com/IIkw1I0fnbzKRH4RZin4ss",
      topics: ["Versetul zilei", "Rugăciuni matinale", "Reflecții serale", "Promisiuni divine"]
    },
    {
      title: "Studii Tematice",
      description: "Analize aprofundate ale marilor teme biblice și doctrinare",
      icon: Crown,
      whatsappLink: "https://chat.whatsapp.com/L5Tj9gTBPsE5oBZTdrw0Yp",
      topics: ["Mântuirea", "Rugăciunea", "Credința", "Dragostea creștină"]
    }
  ];

  return (
    <div className="bg-slate-900 text-white overflow-hidden">
      <SEOHead 
        title="Studii Biblice Creștine cu Mircea Stanciu - Cuvânt și Rugăciune"
        description="Aprofundează-ți înțelegerea Scripturii prin studii biblice sistematice și contemplarea profundă a Cuvântului lui Dumnezeu cu Mircea Stanciu. Studii săptămânale, meditații zilnice și analize tematice."
        keywords="studii biblice romania, biblie studiu, teologie crestina, hermeneutica biblica, mircea stanciu studii, spiritualitate crestina, meditatie biblica"
        canonicalUrl="/studii"
        ogTitle="Studii Biblice Profunde - Cuvânt și Rugăciune cu Mircea Stanciu"
        ogDescription="Explorează Cuvântul lui Dumnezeu prin studii biblice sistematice. Meditații zilnice, studii săptămânale și analize tematice pentru creșterea spirituală."
        structuredData={structuredData}
      />
      
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.7), rgba(15, 23, 42, 0.8)), url('https://images.pexels.com/photos/1029141/pexels-photo-1029141.jpeg')`
          }}
        />
        
        {/* Decorative elements */}
        <div className="absolute top-6 right-6 text-amber-400/10">
          <Shield className="w-20 h-20" />
        </div>
        <div className="absolute bottom-6 left-6 text-amber-400/10">
          <Crown className="w-16 h-16" />
        </div>
        
        <div className="relative container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-4xl mx-auto"
          >
            <div className="flex justify-center items-center space-x-4 mb-6">
              <BookOpen className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
              <Cross className="w-10 h-10 sm:w-16 sm:h-16 text-amber-400" />
              <Star className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
            </div>
            
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500 bg-clip-text text-transparent leading-tight">
              Studii Biblice
            </h1>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-3 sm:mb-4 leading-relaxed px-4"
            >
              Aprofundează-ți înțelegerea Scripturii prin studiul profund al Cuvântului lui Dumnezeu
            </motion.p>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="text-base sm:text-lg text-amber-400 mb-6 sm:mb-8 font-medium"
            >
              "Studiază să te înfățișezi înaintea lui Dumnezeu ca un om încercat" - 2 Timotei 2:15
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Bible Studies Section */}
      <section className="py-16 sm:py-20 bg-slate-800/50 relative">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-400 mb-4">Tipuri de Studii</h2>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto px-4">
              Alege tipul de studiu biblic care se potrivește cel mai bine nevoilor tale spirituale
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {studies.map((study, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                onClick={() => window.open(study.whatsappLink, '_blank')}
                className="bg-slate-700/50 p-6 sm:p-8 rounded-3xl border border-amber-400/20 hover:border-amber-400/40 transition-all duration-300 cursor-pointer group hover:transform hover:scale-105"
              >
                <div className="flex justify-center mb-4">
                  <study.icon className="w-12 h-12 sm:w-16 sm:h-16 text-amber-400 group-hover:text-amber-300 transition-colors" />
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-amber-400 mb-4 text-center">{study.title}</h3>
                <p className="text-sm sm:text-base text-slate-300 mb-6 leading-relaxed text-center">
                  {study.description}
                </p>
                
                <div className="mb-6">
                  <h4 className="text-amber-400 font-semibold mb-3 text-center">Subiecte abordate:</h4>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {study.topics.map((topic, topicIndex) => (
                      <span 
                        key={topicIndex}
                        className="bg-amber-400/20 text-amber-300 px-3 py-1 rounded-full text-xs sm:text-sm"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="text-center">
                  <span className="text-amber-400 hover:text-amber-300 transition-colors font-semibold text-sm sm:text-base">
                    Alătură-te Studiului →
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Study Benefits Section */}
      <section className="py-16 sm:py-20 relative">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-400 mb-4">De Ce Să Studiezi Biblia?</h2>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto px-4">
              Beneficiile studiului biblic în viața creștină
            </p>
          </motion.div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              {
                icon: BookOpen,
                title: "Înțelepciune",
                description: "Dobândește înțelepciune divină pentru viața de zi cu zi"
              },
              {
                icon: Sun,
                title: "Lumină",
                description: "Primește lumină în căutarea ta spirituală"
              },
              {
                icon: Crown,
                title: "Autoritate",
                description: "Înțelege autoritatea Cuvântului lui Dumnezeu"
              },
              {
                icon: Shield,
                title: "Protecție",
                description: "Găsește protecție împotriva înșelăciunii"
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 bg-slate-800/50 rounded-2xl border border-amber-400/20"
              >
                <benefit.icon className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-amber-400 mb-3">{benefit.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default StudiiPage;